import jsPDF from 'jspdf';
import type { IOphthalmologyRecord } from '../interfaces/IMedicalRecord';
import logo from '../assets/logo_documentos.png.png';
import {
  NAVY, GRAY, LGRAY, BLACK,
  v, fmt, f,
  secBox, secBar, colHead, ul, field, examCell,
} from './pdfHelpers';

export interface OphthalmologyExamPDFParams {
  exam: Partial<IOphthalmologyRecord> | null | undefined;
  patientName?: string | null;
  patientCPF?: string | null;
  doctorName?: string | null;
  doctorCRM?: string | null;
  /** ISO date; default: hoje */
  date?: string | null;
}

const PAGE_H = 297; // A4, mm

export async function generateOphthalmologyExamPDF(params: OphthalmologyExamPDFParams): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const LX = 15;  // margem esquerda
  const W  = 180; // largura útil

  // ── CABEÇALHO ────────────────────────────────────────────────────────────────
  let y = 10;
  const logoH = 24;
  const logoW = logoH * (643/388); // proporção real de logo_documentos.png.png
  doc.addImage(logo, 'PNG', (210-logoW)/2, y, logoW, logoH);
  y += 30;

  ul(doc, LX, y, W); y += 8;

  f(doc,'text',NAVY); doc.setFont('helvetica','bold'); doc.setFontSize(15);
  doc.text('EXAME OFTALMOLÓGICO', 105, y, { align:'center' });

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(8.5);
  doc.text('DATA:', LX+W-38, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(fmt(params.date ?? new Date().toISOString()), LX+W-25, y);

  y += 4; ul(doc, LX, y, W); y += 14;

  // ── PACIENTE ───────────────────────────────────────────────────────────────────
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('Paciente:', LX, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','bold'); doc.setFontSize(10.5);
  doc.text(v(params.patientName) || '—', LX+22, y);

  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(9);
  doc.text('CPF:', LX+W-45, y);
  f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(9.5);
  doc.text(v(params.patientCPF) || '—', LX+W-33, y);

  y += 6;

  // ── EXAME OFTALMOLÓGICO (mesma grade do prontuário completo) ────────────────────
  const S3_Y = y; const S3_H = 86;
  secBox(doc, LX, S3_Y, W, S3_H);
  secBar(doc, LX, S3_Y, W, '  ACUIDADE VISUAL / REFRAÇÃO / TONOMETRIA / BIOMICROSCOPIA / FUNDOSCOPIA');

  const C1X = LX+2;  const C1W = 57;
  const C2X = LX+63; const C2W = 58;
  const C3X = LX+125; const C3W = 53;
  const BC_DIVIDER_Y = S3_Y+58; // divisória horizontal entre o bloco B (Tonometria/Biomicroscopia) e C (Campimetria/Paquimetria)

  // divisórias verticais
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(C2X-1.5, S3_Y+7, C2X-1.5, S3_Y+S3_H-1);
  doc.line(C3X-1.5, S3_Y+7,  C3X-1.5, S3_Y+33);
  doc.line(C3X-1.5, BC_DIVIDER_Y, C3X-1.5, S3_Y+S3_H-1);

  const o = params.exam;
  let r3 = S3_Y+10;

  // ─ bloco A ───────────────────────────────────────────────────────────────────
  colHead(doc, C1X, r3, C1W, 'ACUIDADE VISUAL');
  colHead(doc, C2X, r3, C2W, 'REFRAÇÃO');
  colHead(doc, C3X, r3, C3W, 'FUNDOSCOPIA');
  r3 += 7;

  f(doc,'text',GRAY); doc.setFont('helvetica','bold'); doc.setFontSize(6);
  doc.text('SC',  C1X+15, r3, { align:'center' });
  doc.text('CC',  C1X+27, r3, { align:'center' });
  doc.text('P/H', C1X+39, r3, { align:'center' });
  ['ESF','CIL','EIXO','ADD','DNP'].forEach((l,i) =>
    doc.text(l, C2X+9+i*10.8, r3, { align:'center' }));
  r3 += 4;

  // linha OD
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(7);
  doc.text('OD', C1X, r3);
  [[C1X+10,v(o?.visualAcuityOdSc)],[C1X+22,v(o?.visualAcuityOdCc)],[C1X+34,v(o?.visualAcuityOdPh)]].forEach(([x,val]) => {
    ul(doc, x as number, r3+0.8, 10);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val as string, x as number, r3); }
  });
  doc.text('OD', C2X, r3); f(doc,'text',GRAY);
  [v(o?.refractionOdEsf),v(o?.refractionOdCil),v(o?.refractionOdAxis),v(o?.refractionOdAdd),v(o?.refractionOdDnp)].forEach((val,i) => {
    ul(doc, C2X+5+i*10.8, r3+0.8, 8.5);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val, C2X+5+i*10.8, r3); f(doc,'text',GRAY); }
  });
  examCell(doc, 'OD:', v(o?.fundoscopyOd), C3X, r3, C3W-2);
  r3 += 5;

  // linha OE
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(7);
  doc.text('OE', C1X, r3);
  [[C1X+10,v(o?.visualAcuityOeSc)],[C1X+22,v(o?.visualAcuityOeCc)],[C1X+34,v(o?.visualAcuityOePh)]].forEach(([x,val]) => {
    ul(doc, x as number, r3+0.8, 10);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val as string, x as number, r3); }
  });
  doc.text('OE', C2X, r3); f(doc,'text',GRAY);
  [v(o?.refractionOeEsf),v(o?.refractionOeCil),v(o?.refractionOeAxis),v(o?.refractionOeAdd),v(o?.refractionOeDnp)].forEach((val,i) => {
    ul(doc, C2X+5+i*10.8, r3+0.8, 8.5);
    if (val) { f(doc,'text',BLACK); doc.setFontSize(7); doc.text(val, C2X+5+i*10.8, r3); f(doc,'text',GRAY); }
  });
  examCell(doc, 'OE:', v(o?.fundoscopyOe), C3X, r3, C3W-2);
  r3 += 7;

  // divisória horizontal A/B
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(LX+1, r3, LX+W-1, r3); r3 += 3;

  // ─ bloco B ───────────────────────────────────────────────────────────────────
  const bioW = C3X - C2X + C3W;
  colHead(doc, C1X, r3, C1W, 'TONOMETRIA (mmHg)');
  colHead(doc, C2X, r3, bioW, 'BIOMICROSCOPIA');
  r3 += 10;

  examCell(doc, 'OD:', v(o?.tonometryOd),    C1X, r3, C1W-2);
  examCell(doc, 'OD:', v(o?.biomicroscopyOd), C2X, r3, bioW-2);
  r3 += 5;
  examCell(doc, 'OE:', v(o?.tonometryOe),    C1X, r3, C1W-2);
  examCell(doc, 'OE:', v(o?.biomicroscopyOe), C2X, r3, bioW-2);
  r3 += 7;

  // divisória horizontal B/C
  f(doc,'stroke',LGRAY); doc.setLineWidth(0.2);
  doc.line(LX+1, r3, LX+W-1, r3); r3 += 3;

  // ─ bloco C ───────────────────────────────────────────────────────────────────
  colHead(doc, C1X, r3, C1W, 'CAMPIMETRIA');
  colHead(doc, C2X, r3, C2W, 'PAQUIMETRIA (μm)');
  colHead(doc, C3X, r3, C3W, 'OUTROS EXAMES');
  r3 += 10;

  examCell(doc, 'OD:', v(o?.campimetryOd), C1X,    r3, 25);
  examCell(doc, 'OE:', v(o?.campimetryOe), C1X+27, r3, 25);

  const pkOd = o?.pachymetryOd != null ? String(o.pachymetryOd) : '';
  const pkOe = o?.pachymetryOe != null ? String(o.pachymetryOe) : '';
  examCell(doc, 'OD:', pkOd, C2X,    r3, 27);
  examCell(doc, 'OE:', pkOe, C2X+29, r3, 27);

  const others = doc.splitTextToSize(v(o?.otherExams), C3W-2);
  others.slice(0,3).forEach((line: string, i: number) => {
    ul(doc, C3X, r3+0.8+i*4, C3W-2);
    if (line) { f(doc,'text',BLACK); doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.text(line, C3X, r3+i*4); }
  });

  y = S3_Y + S3_H + 3;

  // ── ASSINATURA / RODAPÉ ──────────────────────────────────────────────────────────
  const sigY = Math.max(y + 15, PAGE_H - 45);
  const finalSigY = sigY > PAGE_H - 20 ? PAGE_H - 40 : sigY;

  const sigW = 90;
  const sigX = LX + (W - sigW) / 2;
  ul(doc, sigX, finalSigY, sigW);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6.5);
  doc.text('Assinatura e Carimbo do Médico', sigX+sigW/2, finalSigY+3, { align:'center' });
  field(doc, 'Médico:', v(params.doctorName), sigX, finalSigY+9, sigW/2-2);
  field(doc, 'CRM:', v(params.doctorCRM), sigX+sigW/2+2, finalSigY+9, sigW/2-2);

  const FY = finalSigY + 20;
  ul(doc, LX, FY, W);
  f(doc,'text',GRAY); doc.setFont('helvetica','normal'); doc.setFontSize(6);
  doc.text(
    'Rua Exemplo, 123 - Centro - Sua Cidade/UF - CEP 00000-000     (00) 0000-0000     www.sagradavisao.com.br',
    105, FY+4, { align:'center' },
  );

  // ── SALVAR ────────────────────────────────────────────────────────────────────
  const name     = v(params.patientName ?? 'paciente');
  const datePart = fmt(params.date ?? new Date().toISOString()).replace(/\//g,'-');
  doc.save(`exame_${name.replace(/\s+/g,'_').toLowerCase()}_${datePart}.pdf`);
}
